import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import {
  Button,
  Typography,
  tailwindOverride,
  IconButton,
  Tag,
} from '@getsynapse/design-system';
import { Participant } from 'utils/types/tasksAssignmentsResourcesCapacity';
import { getUserDisplayName, isUserStatusDisabled } from 'utils/functions';
import { PROJECT_PARTICIPANT_TYPE } from 'utils/constants';
import { formatter } from 'utils/functions';
import { setParticipantToUpdate } from 'state/TasksAssignmentsResourcesCapacity/tasksAssignmentsResourcesCapacitySlice';
import { useUserPermissionsContext } from 'Pages/ProjectPage/context/UserPermissionsContext';
import UserAvatar from 'Atoms/UserAvatar';
import { selectUserId } from 'state/User/userSlice';

const ProjectRoles: React.FC<{
  isOwner: boolean;
  roles: string[];
  userId: string;
}> = ({ isOwner, roles, userId }) => {
  return (
    <div className='flex flex-1 flex-col justify-start space-y-3'>
      {isOwner && (
        <Typography variant='caption' className='text-primary'>
          {intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.PROJECT_OWNER_ROLE')}
        </Typography>
      )}
      {!isOwner &&
        roles.length > 0 &&
        roles.map((role, index) => (
          <Typography
            key={`user-${userId}__role-${index}`}
            variant='caption'
            className='text-primary'
          >
            {role}
          </Typography>
        ))}
      {!isOwner && roles.length === 0 && (
        <Typography variant='caption' className='text-primary-dark'>
          {intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.NO_ROLE')}
        </Typography>
      )}
    </div>
  );
};

const UserDetails: React.FC<{
  isOwner: boolean;
  user: Participant;
  hasTasksAssigned?: boolean;
  toggleTaksSection?: () => void;
  isTasksSectionOpen?: boolean;
}> = ({
  isOwner = false,
  user,
  hasTasksAssigned = false,
  toggleTaksSection,
  isTasksSectionOpen = false,
}) => {
  const dispatch = useDispatch();
  const userId = get(user, 'id');
  const roles = get(user, 'job_roles', []);
  const { hasUserPermissionsLevelOf } = useUserPermissionsContext();
  const currentUserId = useSelector(selectUserId);
  const isCurrentUserOwner = hasUserPermissionsLevelOf(
    PROJECT_PARTICIPANT_TYPE.OWNER
  );
  const isCurrentUserMember = hasUserPermissionsLevelOf(
    PROJECT_PARTICIPANT_TYPE.MEMBER
  );
  const canCurrentUserUpdateRoles =
    isCurrentUserOwner || (isCurrentUserMember && currentUserId === userId);
  const handleUpdatRoles = () => {
    dispatch(
      setParticipantToUpdate({
        user_id: userId,
        job_roles: roles,
      })
    );
  };

  const isUserDisabled = useMemo(
    () => isUserStatusDisabled(user.status),
    [user.status]
  );

  return (
    <div className='w-2/5 flex h-full relative'>
      <div className='w-12' />
      <div className='w-2/4 flex py-3 px-2'>
        <div className='w-6'>
          <UserAvatar
            user={{
              avatar_url: get(user, 'avatar_url'),
              data: {
                firstName: get(user, 'data.firstName'),
                lastName: get(user, 'data.lastName'),
                email: get(user, 'data.email'),
              },
              status: user.status,
            }}
          />
        </div>
        <div className='flex flex-1 flex-col ml-2 min-w-0'>
          <div className='flex flex-row justify-between items-center gap-2.5'>
            <Typography
              variant='label'
              className={tailwindOverride(
                'text-primary font-semibold mb-1 whitespace-nowrap overflow-hidden overflow-ellipsis flex-1',
                {
                  'text-neutral-dark': isUserDisabled,
                }
              )}
            >
              {getUserDisplayName(user)}
            </Typography>
            {isUserDisabled && (
              <Tag
                label={intl.get('DEACTIVATED')}
                className='bg-neutral-lighter-two'
                textClassName='text-neutral-dark'
              />
            )}
          </div>
          <Typography variant='label' className='text-neutral break-all mb-2'>
            {get(user, 'data.email')}
          </Typography>
          {!hasTasksAssigned && (
            <Typography variant='caption' className='text-primary-dark'>
              {intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.NO_TASKS')}
            </Typography>
          )}
          {hasTasksAssigned && (
            <Button
              variant='tertiary'
              disabled={!hasTasksAssigned}
              className='text-sm self-start px-1 text-primary'
              onClick={toggleTaksSection}
              size='small'
              data-cy={`participant-${userId}__toggle-tasks-button`}
            >
              {isTasksSectionOpen
                ? intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.HIDE_TASKS')
                : intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.VIEW_TASKS')}
            </Button>
          )}
        </div>
      </div>
      <div
        className={tailwindOverride(
          'w-1/4 px-2 pt-4 pb-3',
          'flex justify-start',
          'space-x-1'
        )}
      >
        <ProjectRoles userId={userId} roles={roles} isOwner={isOwner} />
        {!isOwner && canCurrentUserUpdateRoles && (
          <div className='w-6'>
            <IconButton
              onClick={handleUpdatRoles}
              name='pencil'
              className='text-base text-neutral-dark p-1'
              aria-label={`participant-${userId}__update-roles-button`}
              data-cy={`participant-${userId}__update-roles-button`}
            />
          </div>
        )}
      </div>
      <div className='w-1/4 px-2 py-3'>
        <Typography variant='caption' className='text-primary'>
          {`${formatter.format(user.total_allocation)} ${intl.get(
            'PEOPLE.RESOURCE_ALLOCATION.UPDATE_WEEKLY_ALLOCATION_MODAL.HOUR_LABEL'
          )}`}
        </Typography>
      </div>
    </div>
  );
};

export default UserDetails;
